import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Spin } from 'antd'
import { BulbOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  useAgentBuilderDashboardVersionsQuery,
  useAgentBuilderGroupInsight,
  useAgentBuilderWidgetInsights,
} from '../hooks'
import { buildWidgetInsightData } from '../presentation'
import { hasInsightContent, parseInsightSections, type InsightSections } from '../insightSections'
import type { WidgetSessionState } from '../viewer/engine/state'
import type {
  AgentBuilderGroupInsightPayload,
  AgentBuilderWidgetInsightPayload,
  DashboardDefinition,
  WidgetHydrationState,
} from '../types'

function toPresentationState(session: WidgetSessionState): WidgetHydrationState {
  return {
    status: session.status === 'error' && session.data.rows.length === 0 ? 'error' : 'live',
    stage: 'execute_dax_query',
    rows: session.data.rows,
    columns: session.data.columns,
    chartData: session.data.chartData,
    kpiCards: session.data.kpiCards,
    daxQuery: session.data.daxQuery,
    error: session.error,
    hasPaginationMetadata: session.data.hasPaginationMetadata,
    totalRows: session.data.totalRows,
    page: session.data.page,
    pageSize: session.data.pageSize,
    sortColumn: session.data.sortColumn,
    sortDirection: session.data.sortDirection,
    groupByDimension: session.query.groupByDimension ?? session.data.groupByDimension,
    axisMinValue: session.data.axisMinValue,
    axisMaxValue: session.data.axisMaxValue,
    axisMinByMeasure: session.data.axisMinByMeasure,
    axisMaxByMeasure: session.data.axisMaxByMeasure,
    columnFormats: session.data.columnFormats,
    lastUpdatedAt: session.data.lastUpdatedAt,
  }
}

function pickInsightBullets(payload: { keyFindings?: string[]; recommendations?: string[]; insight?: string } | undefined | null): InsightSections {
  const cleanList = (list: unknown) => (Array.isArray(list)
    ? list.map((entry) => String(entry ?? '').trim()).filter(Boolean)
    : [])
  const keyFindings = cleanList(payload?.keyFindings)
  const recommendations = cleanList(payload?.recommendations)

  if (keyFindings.length > 0 || recommendations.length > 0) {
    return { keyFindings, recommendations, hasSections: true }
  }

  return parseInsightSections(payload?.insight)
}

interface AgentBuilderDashboardInsightsProps {
  clientId: string
  dashboardId: string | null
  dashboard: DashboardDefinition | null
  widgetSessions?: Record<string, WidgetSessionState>
  insightSignature?: string | null
  widgetHydrationStates?: Record<string, WidgetHydrationState>
  printMode?: boolean
  /** `stale` is true when filters changed since the displayed insights were generated. */
  onReadyChange?: (ready: boolean, signature: string | null, stale: boolean) => void
  onInsightReady?: (insight: string | null) => void
}

export function AgentBuilderDashboardInsights({
  clientId,
  dashboardId,
  dashboard,
  widgetSessions,
  insightSignature,
  widgetHydrationStates,
  printMode = false,
  onReadyChange,
  onInsightReady,
}: AgentBuilderDashboardInsightsProps) {
  // The signature the currently displayed insights were generated for. `null`
  // until the first generation completes. Insights are (re)generated only when
  // this changes: once automatically on initial load, then on demand when the
  // user clicks "Re-generate" — never automatically as filters change.
  // Stored with the dashboard root id it was activated for, so a signature
  // from a previously viewed dashboard is never treated as active for another.
  const [activeInsight, setActiveInsight] = useState<{ rootId: string; signature: string } | null>(null)
  // Bumped on every "Re-generate" click and folded into the insight query keys,
  // so a regeneration always gets fresh queries (and re-queries the model) even
  // when the filter signature is unchanged.
  const [generation, setGeneration] = useState(0)

  const versionsQuery = useAgentBuilderDashboardVersionsQuery(clientId, dashboardId)
  const dashboardRootId = versionsQuery.data?.[versionsQuery.data.length - 1]?.rootId ?? null

  // Switching to a different dashboard resets everything so the new dashboard
  // generates its own insights from scratch: an activation for another root id
  // simply does not count. `generation` is left as is; it only ever increases,
  // so query keys stay unique.
  const activeSignature = activeInsight !== null && activeInsight.rootId === dashboardRootId
    ? activeInsight.signature
    : null
  const insightQueryScope = activeSignature === null ? null : `${activeSignature}#${generation}`

  const usingEngineSessions = Boolean(widgetSessions)
  const nonInsightWidgetIds = useMemo(
    () => (dashboard?.widgets ?? []).filter((w) => w.type !== 'insight').map((w) => w.id),
    [dashboard],
  )

  const allWidgetsHydrated =
    nonInsightWidgetIds.length > 0 &&
    nonInsightWidgetIds.every((id) => {
      const status = usingEngineSessions
        ? widgetSessions?.[id]?.status
        : widgetHydrationStates?.[id]?.status
      return status === 'live' || status === 'error'
    })

  const hasAnyLiveWidget = nonInsightWidgetIds.some((id) => (
    usingEngineSessions
      ? widgetSessions?.[id]?.status === 'live'
      : widgetHydrationStates?.[id]?.status === 'live'
  ))

  const fallbackInsightSignature = useMemo(() => {
    if (usingEngineSessions || !allWidgetsHydrated || !hasAnyLiveWidget) return null
    return nonInsightWidgetIds
      .filter((id) => widgetHydrationStates?.[id]?.status === 'live')
      .map((id) => `${id}:${widgetHydrationStates?.[id]?.rows.length ?? 0}`)
      .join('|')
  }, [allWidgetsHydrated, hasAnyLiveWidget, nonInsightWidgetIds, usingEngineSessions, widgetHydrationStates])

  const effectiveInsightSignature = usingEngineSessions ? (insightSignature ?? null) : fallbackInsightSignature

  const insightsTriggered = activeSignature !== null
  // The user changed filters (or the underlying data moved) since the currently
  // displayed insights were generated. We keep showing the previous insights
  // until they click "Re-generate".
  const isStale =
    activeSignature !== null &&
    effectiveInsightSignature !== null &&
    effectiveInsightSignature !== activeSignature

  // Initial generation only: once the widgets are ready we generate insights a
  // single time. After that, regeneration is manual (the "Re-generate" button).
  useEffect(() => {
    if (activeSignature !== null) return
    if (!effectiveInsightSignature || !dashboardRootId || !allWidgetsHydrated || !hasAnyLiveWidget) return
    setActiveInsight({ rootId: dashboardRootId, signature: effectiveInsightSignature })
  }, [activeSignature, allWidgetsHydrated, dashboardRootId, effectiveInsightSignature, hasAnyLiveWidget])

  const canRegenerate = Boolean(effectiveInsightSignature && dashboardRootId)
  const handleRegenerate = useCallback(() => {
    if (!effectiveInsightSignature || !dashboardRootId) return
    setGeneration((n) => n + 1)
    setActiveInsight({ rootId: dashboardRootId, signature: effectiveInsightSignature })
  }, [dashboardRootId, effectiveInsightSignature])

  const liveWidgetPayloads = useMemo((): AgentBuilderWidgetInsightPayload[] => {
    if (!dashboardRootId || !dashboard) return []
    return (dashboard.widgets ?? []).flatMap((w) => {
      const session = widgetSessions?.[w.id]
      const dataState = usingEngineSessions
        ? (session ? toPresentationState(session) : null)
        : (widgetHydrationStates?.[w.id] ?? null)
      // Widgets with no rows have nothing to analyse, so they never get an insight.
      if (w.type === 'insight' || !dataState || dataState.status !== 'live' || (dataState.rows?.length ?? 0) === 0) {
        return []
      }

      const data = buildWidgetInsightData(w, dataState)
      return [{
        clientId,
        agentBuilderDashboardRootId: dashboardRootId,
        widgetId: w.id,
        widgetTitle: w.title,
        widgetDescription: w.description,
        widgetType: w.type,
        chartData: data.chartData,
        kpiCards: data.kpiCards,
        valueFormats: data.valueFormats,
        columns: dataState.columns ?? [],
        rows: dataState.rows.slice(0, 1000),
      }]
    })
  }, [clientId, dashboard, dashboardRootId, usingEngineSessions, widgetHydrationStates, widgetSessions])

  const widgetInsightResults = useAgentBuilderWidgetInsights(
    insightsTriggered ? liveWidgetPayloads : [],
    insightsTriggered,
    insightQueryScope,
  )

  const allWidgetInsightsDone =
    insightsTriggered &&
    widgetInsightResults.length > 0 &&
    widgetInsightResults.every((r) => r.status === 'success' || r.status === 'error')

  const groupInsightPayload = useMemo((): AgentBuilderGroupInsightPayload | null => {
    if (!allWidgetInsightsDone || !dashboardRootId) return null
    const widgetInsights = widgetInsightResults
      .map((r, i) => ({
        widgetId: liveWidgetPayloads[i]?.widgetId ?? '',
        widgetTitle: liveWidgetPayloads[i]?.widgetTitle ?? '',
        insightText: r.data?.insight ?? '',
      }))
      .filter((w) => !!w.insightText)
    if (widgetInsights.length === 0) return null
    return {
      clientId,
      agentBuilderDashboardRootId: dashboardRootId,
      dashboardTitle: dashboard?.title ?? '',
      intentKey: dashboard?.intentKey ?? '',
      widgetInsights,
    }
  }, [allWidgetInsightsDone, clientId, dashboard, dashboardRootId, liveWidgetPayloads, widgetInsightResults])

  const groupInsightResult = useAgentBuilderGroupInsight(groupInsightPayload, allWidgetInsightsDone, insightQueryScope)

  const groupSections = groupInsightResult.status === 'success' ? pickInsightBullets(groupInsightResult.data) : null
  const noInsightData =
    (allWidgetsHydrated && !hasAnyLiveWidget) ||
    (insightsTriggered && liveWidgetPayloads.length === 0) ||
    (allWidgetInsightsDone && groupInsightPayload === null) ||
    (groupSections !== null && !hasInsightContent(groupSections))

  const isGenerating =
    insightsTriggered &&
    !noInsightData &&
    (!allWidgetInsightsDone || groupInsightResult.status === 'pending')

  const showWaiting = !insightsTriggered && !allWidgetsHydrated
  const showWaitingForDashboard = !dashboardId || versionsQuery.isLoading || !dashboardRootId
  const insightsReady =
    !showWaitingForDashboard &&
    (noInsightData || (insightsTriggered && allWidgetInsightsDone && groupInsightResult.status === 'success'))

  useEffect(() => {
    // Report readiness against the signature the insights were actually
    // generated for. When filters have changed (isStale) the displayed insights
    // no longer match the current view, so we report "not ready" — this keeps
    // downstream consumers (e.g. PDF export) from using stale insights.
    onReadyChange?.(insightsReady && !isStale, activeSignature, isStale)
  }, [activeSignature, insightsReady, isStale, onReadyChange])

  useEffect(() => {
    if (noInsightData) {
      onInsightReady?.(null)
      return
    }

    if (groupInsightResult.status === 'success') {
      onInsightReady?.(groupInsightResult.data?.insight ?? null)
      return
    }

    if (groupInsightResult.status === 'error') {
      onInsightReady?.(null)
    }
  }, [groupInsightResult.data?.insight, groupInsightResult.status, noInsightData, onInsightReady])

  if (!dashboard) return null

  // Same mock-up card chrome as the widget cards (screen only).
  const cardChromeStyles = printMode
    ? undefined
    : {
        root: {
          background: '#fff',
          borderRadius: 10,
          border: '1px solid #e6eaef',
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
          overflow: 'hidden',
        } as const,
        header: {
          background: '#fff',
          borderBottom: '1px solid #e6eaef',
          minHeight: 50,
          padding: '0 24px',
        } as const,
        title: { padding: 0 } as const,
        body: { padding: '18px 24px 24px' } as const,
      }

  return (
    <Card
      style={cardChromeStyles?.root}
      styles={cardChromeStyles ? { header: cardChromeStyles.header, title: cardChromeStyles.title, body: cardChromeStyles.body } : undefined}
      title={
        <span className="flex items-center gap-2.5 text-[15px] font-semibold leading-tight text-[#1d2433]">
          <span className="h-[18px] w-[3px] rounded-full bg-[var(--feature-primary)]" aria-hidden />
          <BulbOutlined className="feature-icon-accent" />
          Dashboard Insights
        </span>
      }
      extra={
        !printMode && allWidgetInsightsDone && groupInsightResult.status !== 'pending' ? (
          <Button
            size="small"
            type={isStale ? 'primary' : 'default'}
            icon={<ReloadOutlined />}
            onClick={handleRegenerate}
            disabled={!canRegenerate}
          >
            Re-generate
          </Button>
        ) : null
      }
    >
      {showWaitingForDashboard && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
          <Spin size="small" />
          Preparing dashboard insights...
        </div>
      )}

      {!showWaitingForDashboard && showWaiting && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
          <Spin size="small" />
          Refreshing data…
        </div>
      )}

      {!showWaitingForDashboard && noInsightData && (
        <div className="py-6 text-center text-sm text-gray-400">
          No insights available. There is no data to analyse for the current view.
        </div>
      )}

      {!showWaitingForDashboard && insightsTriggered && !noInsightData && (
        <div>
          {isStale && !isGenerating && (
            <Alert
              type="info"
              showIcon
              className="mb-3 text-xs"
              message="Filters have changed since these insights were generated. Click Re-generate to update them."
            />
          )}

          {isGenerating && (
            <span className="flex items-center gap-2 text-sm text-gray-400">
              <Spin size="small" />
              Generating insights…
            </span>
          )}

          {allWidgetInsightsDone && (
            <>
              {groupInsightResult.status === 'pending' && (
                <span className="flex items-center gap-2 text-sm text-gray-400">
                  <Spin size="small" />
                  Synthesising dashboard insight…
                </span>
              )}
              {groupSections && (() => {
                const { keyFindings, recommendations, hasSections } = groupSections
                return (
                  <div className="space-y-4">
                    {keyFindings.length > 0 ? (
                      <div>
                        {hasSections ? (
                          <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">Key findings</h4>
                        ) : null}
                        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-700">
                          {keyFindings.map((entry) => (
                            <li key={`kf-${entry}`}>{entry}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {recommendations.length > 0 ? (
                      <div>
                        <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-gray-500">Recommendations</h4>
                        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-700">
                          {recommendations.map((entry) => (
                            <li key={`rec-${entry}`}>{entry}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )
              })()}
              {groupInsightResult.status === 'error' && (
                <Alert type="error" message="Could not generate dashboard insight." className="text-xs" />
              )}
            </>
          )}
        </div>
      )}
    </Card>
  )
}
